import React from 'react'
import PropTypes from 'prop-types'
import { useTranslate } from '@ds/comp-private'
import { dataProps, onProps } from '@ds/react-utils'
import { Close, CloseOlive, CloseSmall } from '@ds/icons'
import { Button } from '../../../components/Button'
import { IconWithTheme } from '../IconWithTheme'
import { useThemeStyles } from '../../../theming'
import { CustomPropTypes } from '../../../support'
import type { EventListenerProps, ButtonForwardRef } from '../../../types'
import styles from './styles'

export interface PopoverCloseProps
  extends EventListenerProps<HTMLButtonElement> {
  /**
   * The text to present to assistive devices in order to identify the PopoverClose.
   */
  accessibilityText?: string
  /**
   * Set to true to use PopoverClose on dark backgrounds.
   */
  dark?: boolean
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * A React ref to assign to the HTML node representing the PopoverClose element.
   */
  forwardedRef?: ButtonForwardRef
  /**
   *  Adds an optional scrim styled wrapper around the PopoverClose for when it sits on top of an image. IE ModalClose in ImageModal.
   */
  scrim?: boolean
  /**
   *  A smaller version of PopoverClose the utilizes the IconSmall component.
   */
  small?: boolean
}

export function PopoverClose({
  accessibilityText,
  dark = false,
  forwardedRef,
  scrim = false,
  onClick,
  small = false,
  ...restProps
}: PopoverCloseProps) {
  const sx = useThemeStyles(styles, { small, dark, scrim })
  const translate = useTranslate()

  return (
    <div css={sx.default.wrap}>
      <Button
        {...dataProps(restProps)}
        {...onProps(restProps)}
        accessibilityText={accessibilityText || translate('OLIVE:CLOSE')}
        forwardedRef={forwardedRef}
        kind="tertiary"
        onClick={onClick}
        startElement={
          <IconWithTheme
            inkIcon={small ? <CloseSmall /> : <Close />}
            oliveIcon={<CloseOlive />}
          />
        }
      />
    </div>
  )
}

PopoverClose.propTypes = {
  accessibilityText: PropTypes.string,
  dark: PropTypes.bool,
  'data-.*': PropTypes.string,
  forwardedRef: CustomPropTypes.ReactRef,
  'on[A-Z].*': PropTypes.func,
  onClick: PropTypes.func,
  scrim: PropTypes.bool,
  small: PropTypes.bool,
}

PopoverClose.displayName = 'PopoverClose'
